'use client'

import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ToastType = 'success' | 'error' | 'info' | 'warning'

interface ToastItem {
  id: number
  message: string
  type: ToastType
}

interface ToastContextValue {
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
  warning: (message: string) => void
}

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a <ToastProvider>')
  return ctx
}

/* ------------------------------------------------------------------ */
/*  Standalone toast (module-level, works without hook)                 */
/* ------------------------------------------------------------------ */

let _dispatch: ToastContextValue | null = null

export const toast = {
  success: (msg: string) => _dispatch?.success(msg),
  error: (msg: string) => _dispatch?.error(msg),
  info: (msg: string) => _dispatch?.info(msg),
  warning: (msg: string) => _dispatch?.warning(msg),
}

/* ------------------------------------------------------------------ */
/*  Styling                                                            */
/* ------------------------------------------------------------------ */

const ICON: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  info: 'ℹ',
  warning: '⚠',
}

const COLORS: Record<ToastType, { bg: string; border: string; text: string; icon: string }> = {
  success: {
    bg: 'rgba(16, 185, 129, 0.1)',
    border: '1px solid rgba(16, 185, 129, 0.3)',
    text: '#065f46',
    icon: '#10b981',
  },
  error: {
    bg: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    text: '#991b1b',
    icon: '#ef4444',
  },
  info: {
    bg: 'rgba(59, 130, 246, 0.1)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    text: '#1e40af',
    icon: '#3b82f6',
  },
  warning: {
    bg: 'rgba(245, 158, 11, 0.1)',
    border: '1px solid rgba(245, 158, 11, 0.3)',
    text: '#92400e',
    icon: '#f59e0b',
  },
}

const DURATION = 4000

/* ------------------------------------------------------------------ */
/*  Toast Item Component                                               */
/* ------------------------------------------------------------------ */

function ToastMessage({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  const colors = COLORS[item.type]

  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 16px',
        borderRadius: '8px',
        background: colors.bg,
        border: colors.border,
        color: colors.text,
        fontSize: '14px',
        lineHeight: '1.4',
        maxWidth: '420px',
        width: '100%',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        backdropFilter: 'blur(8px)',
        animation: 'toast-in 0.25s ease-out',
        cursor: 'pointer',
      }}
      onClick={() => onDismiss(item.id)}
    >
      <span style={{ color: colors.icon, fontSize: '16px', fontWeight: 700, flexShrink: 0 }}>
        {ICON[item.type]}
      </span>
      <span style={{ flex: 1 }}>{item.message}</span>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Provider                                                           */
/* ------------------------------------------------------------------ */

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const idRef = useRef(0)

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const show = useCallback(
    (message: string, type: ToastType) => {
      const id = ++idRef.current
      setToasts((prev) => [...prev, { id, message, type }])
      setTimeout(() => dismiss(id), DURATION)
    },
    [dismiss],
  )

  const api: ToastContextValue = {
    success: useCallback((m: string) => show(m, 'success'), [show]),
    error: useCallback((m: string) => show(m, 'error'), [show]),
    info: useCallback((m: string) => show(m, 'info'), [show]),
    warning: useCallback((m: string) => show(m, 'warning'), [show]),
  }

  // Keep module-level dispatch in sync
  _dispatch = api

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/* CSS animation */}
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(-8px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Toast container */}
      {toasts.length > 0 && (
        <div
          aria-live="polite"
          style={{
            position: 'fixed',
            top: '16px',
            right: '16px',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            pointerEvents: 'auto',
          }}
        >
          {toasts.map((t) => (
            <ToastMessage key={t.id} item={t} onDismiss={dismiss} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  )
}
